import { Button, Grid } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Analyzer, CreateSnapshotDto, Snapshot } from 'types';

import { updateAnalyzerProperties } from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';

import {
  AnalyzerMode,
  SnapshotTraceHistoryCombination,
} from '../analyzers/types';

import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useDeleteSnapshot } from 'features/snapshots/api/deleteSnapshot';
import { useUpdateAnalyzer } from '../analyzers/api/updateAnalyzer';
import { useWindow } from 'features/windows/api/getWindow';
import { useUpdateWindow } from 'features/windows/api/updateWindow';
import { useCreateSnapshot } from './api/createSnapshot';
import { useUpdateSnapshot } from './api/updateSnapshot';

interface Props {
  selectedSnapshot: SnapshotTraceHistoryCombination;
  selectedAnalyzer: Analyzer;
  userId: number;
  setSelectedSnapshotId?: Dispatch<SetStateAction<number>>;
  inDialog: boolean | undefined;
}

const SnapshotSaveButton = ({
  selectedSnapshot,
  selectedAnalyzer,
  userId,
  setSelectedSnapshotId,
  inDialog,
}: Props) => {
  const dispatch = useAppDispatch();

  const { selectedWindowId } = useAppSelector((state) => state.windows);
  const { data: selectedWindow } = useWindow({ windowId: selectedWindowId });
  const { data: lastSnapshot } = useSnapshot({
    snapshotId: selectedSnapshot?.temporary_snapshot_id || -1,
  });
  const updateAnalyzerMutation = useUpdateAnalyzer({});
  const deleteSnapshotMutation = useDeleteSnapshot({
    analyzerId: selectedAnalyzer.id,
  });
  const updateWindowMutation = useUpdateWindow({});
  const createSnapshotMutation = useCreateSnapshot({
    analyzerId: selectedAnalyzer.id,
  });
  const updateSnapshotMutation = useUpdateSnapshot({});

  // Remove button and functionality if analyzer is in polling mode
  if (selectedAnalyzer?.mode !== AnalyzerMode.LIVE) return null;

  const createTemporarySnapshot = async () => {
    const snapshotToSave: CreateSnapshotDto = {
      ...(selectedSnapshot as Snapshot),
      name: `${selectedSnapshot.name} TEMP`,
      enabled: false,
      temporary_snapshot_id: selectedSnapshot.id,
    };
    return await createSnapshotMutation.mutateAsync({
      data: snapshotToSave,
    });
  };

  const switchAnalyzerToTemporarySnapshot = async (savedSnapshot: Snapshot) => {
    updateAnalyzerMutation.mutate({
      data: {
        ...selectedAnalyzer,
        active_snapshot: savedSnapshot.id,
        enabled: !!selectedAnalyzer.enabled,
      },
      analyzerId: selectedAnalyzer.id,
    });

    dispatch(
      updateAnalyzerProperties({
        user_id: userId,
        properties: savedSnapshot,
        analyzer: selectedAnalyzer,
      })
    );
  };

  const switchWindowToTemporarySnapshot = async (savedSnapshot: Snapshot) => {
    if (inDialog && setSelectedSnapshotId) {
      setSelectedSnapshotId(savedSnapshot.id);
    } else {
      if (!selectedWindow) return;
      await updateWindowMutation.mutateAsync({
        data: {
          ...selectedWindow,
          snapshot_id: savedSnapshot.id,
        },
        windowId: selectedWindow.id,
      });
    }
  };

  const handleCreateTemporarySnapshotClicked = async () => {
    const savedSnapshot = await createTemporarySnapshot();
    await switchAnalyzerToTemporarySnapshot(savedSnapshot);
    await switchWindowToTemporarySnapshot(savedSnapshot);
  };

  const resetAnalyzerToParentSnapshot = async () => {
    if (!lastSnapshot) return;
    await updateAnalyzerMutation.mutateAsync({
      data: {
        ...selectedAnalyzer,
        active_snapshot: selectedSnapshot.temporary_snapshot_id,
        enabled: !!selectedAnalyzer.enabled,
      },
      analyzerId: selectedAnalyzer.id,
    });

    dispatch(
      updateAnalyzerProperties({
        user_id: userId,
        properties: lastSnapshot,
        analyzer: selectedAnalyzer,
      })
    );
  };

  const resetWindowToParentSnapshot = async () => {
    if (selectedWindow === undefined) return;
    if (
      inDialog &&
      setSelectedSnapshotId &&
      selectedSnapshot.temporary_snapshot_id
    ) {
      setSelectedSnapshotId(selectedSnapshot.temporary_snapshot_id);
    } else {
      await updateWindowMutation.mutateAsync({
        data: {
          ...selectedWindow,
          snapshot_id: selectedSnapshot.temporary_snapshot_id,
        },
        windowId: selectedWindow.id,
      });
    }
  };

  const handleRevertTemporarySnapshotClicked = async () => {
    await resetAnalyzerToParentSnapshot();
    await resetWindowToParentSnapshot();
    deleteSnapshotMutation.mutate(selectedSnapshot.id);
  };

  const handleCommitTemporarySnapshotClicked = async () => {
    await updateSnapshotMutation.mutateAsync({
      snapshotId: selectedSnapshot.id,
      data: { temporary_snapshot_id: null },
    });
  };

  const CreateTemporarySnapshotButton = (
    <Grid item style={{ minWidth: '140px' }}>
      <Button
        variant='outlined'
        onClick={handleCreateTemporarySnapshotClicked}
        fullWidth
      >
        Temp Snapshot
      </Button>
    </Grid>
  );

  const RevertOrCommitTemporarySnapshotButtons = (
    <>
      <Grid item>
        <Button
          variant='outlined'
          color='secondary'
          onClick={handleRevertTemporarySnapshotClicked}
          fullWidth
        >
          Revert
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant='outlined'
          color='primary'
          onClick={handleCommitTemporarySnapshotClicked}
          fullWidth
        >
          Make Permanent
        </Button>
      </Grid>
    </>
  );

  return (
    <Grid
      container
      item
      direction='row'
      wrap='nowrap'
      spacing={1}
      style={{
        minWidth: selectedSnapshot.temporary_snapshot_id ? '200px' : '140px',
      }}
    >
      {!selectedSnapshot.temporary_snapshot_id
        ? CreateTemporarySnapshotButton
        : RevertOrCommitTemporarySnapshotButtons}
    </Grid>
  );
};

export default SnapshotSaveButton;
